import * as Yup from "yup";
import React, { useEffect, useRef, useState } from "react";
import { FiMail } from "react-icons/fi";
import { Formik } from "formik";
import { MdClose, MdLocalPhone } from "react-icons/md";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import { cellPhoneNumberRegex } from "utils/regex";
import { registerValues } from "store/actions/first-access-mi-actions";

import {
  ContentButtons,
  ContentField,
  ContentToken,
  Field,
  FormContainer,
  FormRadio,
  FormRadioContent,
  ModalContainer,
  ModalHeader,
  ModalStyle,
  Paragraph,
  SelectTypeValidation,
  TextLabel,
  TextTimer,
  Title,
} from "./styles";
import { getAccessToken } from "services/accessTokenServices";

const formatTimer = (timer): string => {
  // @ts-ignore
  let minutes = parseInt(timer / 60, 10);
  // @ts-ignore
  let seconds = parseInt(timer % 60, 10);
  // @ts-ignore
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  // @ts-ignore
  seconds = seconds < 10 ? `0${seconds}` : seconds;
  return `${minutes}:${seconds}`;
};

const ValidationContact = ({
  show,
  handleCloseModal,
  accessToken,
  finishNewContact,
}: any): JSX.Element => {
  const [step, setStep] = useState("validationOption");
  const [validationType, setValidationType] = useState("");
  const [validationValue, setValidationValue] = useState("");
  const [timer, setTimer] = useState(60);
  const [startTimer, setStartTimer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectTypeValidation, setSelectTypeValidation] = useState("email");

  const { location, customerCode, document, siteId } = useSelector(
    // @ts-ignore
    (state) => state.firstAccessReducer
  );
  const dispatch = useDispatch();

  const intervalRef = useRef();
  let newTimer = 60;

  const decreaseNum = (): void => {
    if (newTimer === 0) {
      setStartTimer(false);
      clearInterval(intervalRef.current);
      return;
    }
    setTimer((prev) => {
      newTimer -= 1;
      return prev - 1;
    });
  };

  useEffect(() => {
    if (startTimer) {
      // @ts-ignore
      intervalRef.current = setInterval(decreaseNum, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [startTimer]);

  const sendToken = async (type, value): Promise<any> => {
    try {
      const access_token = (await getAccessToken()) || "";
      const headers = {
        access_token,
      };
      const bodyCreteContact = {
        contact: value,
        contactType: type,
        customerCode,
        siteId,
        document,
      };

      const result = await api.post(`${location}/contacts`, bodyCreteContact, {
        headers,
      });
      const contactId = result.headers.location.split("/").pop();

      dispatch(registerValues({ contactId }));

      const bodyToken = {
        tokenType: "REGISTER",
        url: "",
        contactId,
        customerCode,
        document,
      };

      const responseToken = await api.post(`${location}/tokens`, bodyToken);

      toast.warn(`Enviamos um código de verificação para ${value}`);
      return responseToken;
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
      return false;
    }
  };

  const validationToken = async (token): Promise<any> => {
    const userId = location.split("/").pop();
    const headers = {
      access_token: accessToken,
    };
    const bodyToken = {
      token,
    };

    try {
      await api.post(
        `industrial/customer/v1/users/${userId}/validations`,
        bodyToken,
        {
          headers,
        }
      );

      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ?? "Ocorreu um erro interno na aplicação"
      );

      return false;
    }
  };

  const handlerSubmitToken = async (data): Promise<void> => {
    setLoading(true);

    try {
      const response = await validationToken(data.token);

      if (response) {
        finishNewContact();
      }
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;

        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }

    setLoading(false);
  };

  const handlerSubmitEmail = async (data): Promise<void> => {
    setLoading(true);
    await sendToken("EMAIL", data.email);
    setStep("validationToken");
    setValidationType("EMAIL");
    setValidationValue(data.email);
    setStartTimer(true);
    setLoading(false);
  };

  const handlerSubmitPhone = async (data): Promise<void> => {
    setLoading(true);
    const value = data.phoneNumber.replace(/[^\d]/g, "");
    await sendToken("PHONE", value);
    setStep("validationToken");
    setValidationType("PHONE");
    setValidationValue(value);
    setStartTimer(true);
    setLoading(false);
  };

  const resetStep = (): void => {
    setStep("validationOption");
    setStartTimer(false);
    setTimer(60);
  };

  const resendToken = async (): Promise<void> => {
    setLoading(true);
    setTimer(60);
    setStartTimer(true);
    await sendToken(validationType, validationValue);
    setLoading(false);
  };

  const onHide = (): void => {
    handleCloseModal();
    resetStep();
  };

  const validatorSchemaEmail = (): any =>
    Yup.object().shape({
      email: Yup.string()
        .email("E-mail inválido")
        .required("É necessário um e-mail"),
    });

  const validatorSchemaToken = (): any =>
    Yup.object().shape({
      token: Yup.string().required("É necessário um token"),
    });

  const validatorSchemaPhone = (): any =>
    Yup.object().shape({
      phoneNumber: Yup.string()
        .required("É necessário digitar seu número de celular")
        .test("Telefone", "Número de telefone inválido", (value) => {
          if (value) {
            value = value.replace(/[^\d]/g, "");
            return !!value.match(cellPhoneNumberRegex);
          }
          return null;
        }),
    });

  return (
    <ModalStyle
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalContainer>
        <ModalHeader>
          <Title>Dados cadastrais</Title>
          <MdClose size={24} onClick={handleCloseModal} />
        </ModalHeader>
        <Paragraph>
          Valide pelo <b>menos um de seus contatos</b> pessoais
        </Paragraph>

        {step === "validationOption" && (
          <div>
            <SelectTypeValidation>
              <FormRadioContent>
                <FormRadio
                  custom
                  type="radio"
                  id="email"
                  name="type"
                  label="Email"
                  isSelect={selectTypeValidation === "email"}
                  onClick={() => setSelectTypeValidation("email")}
                />
                <FormRadio
                  custom
                  type="radio"
                  id="phone"
                  name="type"
                  label="Celular"
                  isSelect={selectTypeValidation === "phone"}
                  onClick={() => setSelectTypeValidation("phone")}
                />
              </FormRadioContent>
            </SelectTypeValidation>
            {selectTypeValidation === "email" && (
              <Formik
                initialValues={{
                  email: "",
                }}
                onSubmit={handlerSubmitEmail}
                validationSchema={validatorSchemaEmail}
                render={({
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <FormContainer>
                    <span>Um código será enviado no email informado</span>
                    <ContentField>
                      <Field>
                        <TextField
                          LeadingIcon={FiMail}
                          propsLeadingIcon={{
                            size: 24,
                          }}
                          type="text"
                          name="email"
                          label="Email *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          errorCondition={!!errors.email && !!touched.email}
                          errorMessage={`${errors.email}`}
                          disabled={loading}
                        />
                      </Field>
                      <OutlinedButton
                        type="submit"
                        disabled={!values.email || !!errors.email || loading}
                      >
                        Enviar token
                      </OutlinedButton>
                    </ContentField>
                  </FormContainer>
                )}
              />
            )}
            {selectTypeValidation === "phone" && (
              <Formik
                initialValues={{
                  phoneNumber: "",
                }}
                onSubmit={handlerSubmitPhone}
                validationSchema={validatorSchemaPhone}
                render={({
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <FormContainer>
                    <span>Um código será enviado no celular informado</span>
                    <ContentField>
                      <Field>
                        <TextField
                          LeadingIcon={MdLocalPhone}
                          propsLeadingIcon={{
                            size: 24,
                          }}
                          name="phoneNumber"
                          mask="(99) 9 9999-9999"
                          label="Celular com DDD *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phoneNumber}
                          errorCondition={
                            !!errors.phoneNumber && !!touched.phoneNumber
                          }
                          errorMessage={`${errors.phoneNumber}`}
                          disabled={loading}
                        />
                      </Field>
                      <OutlinedButton
                        type="submit"
                        disabled={
                          !values.phoneNumber || !!errors.phoneNumber || loading
                        }
                      >
                        Enviar token
                      </OutlinedButton>
                    </ContentField>
                  </FormContainer>
                )}
              />
            )}
          </div>
        )}
        {step === "validationToken" && (
          <ContentToken>
            <TextLabel>
              {validationType === "EMAIL" &&
                "O código foi enviado para o email informado"}
              {validationType === "PHONE" && "O código foi enviado por SMS"}
            </TextLabel>
            <Formik
              initialValues={{
                token: "",
              }}
              onSubmit={handlerSubmitToken}
              validationSchema={validatorSchemaToken}
              render={({
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
              }) => (
                <FormContainer>
                  <Field>
                    <TextField
                      name="token"
                      label="Inserir código *"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.token}
                      errorCondition={!!errors.token && !!touched.token}
                      errorMessage={`${errors.token}`}
                      disabled={loading}
                    />
                  </Field>
                  <TextTimer>
                    Não recebi o código.{" "}
                    {timer > 0 ? (
                      `Aguarde ${formatTimer(timer)}`
                    ) : (
                      <a href="#" onClick={resendToken}>
                        Enviar novamente
                      </a>
                    )}
                  </TextTimer>
                  <ContentButtons id="content-buttons">
                    <OutlinedButton
                      disabled={!values.token || !!errors.token || loading}
                      type="submit"
                    >
                      Validar token
                    </OutlinedButton>
                    {timer <= 0 && (
                      <OutlinedButton onClick={resetStep} disabled={loading}>
                        Trocar forma de validação
                      </OutlinedButton>
                    )}
                  </ContentButtons>
                </FormContainer>
              )}
            />
          </ContentToken>
        )}
      </ModalContainer>
    </ModalStyle>
  );
};

export default ValidationContact;
